import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, User, FileText, Download, RefreshCw } from "lucide-react";
import SignatureCapture from "@/components/SignatureCapture";

const translations = {
  en: {
    backToDashboard: "Back to Dashboard",
    profileSettings: "Profile Settings",
    profileTab: "Profile",
    contractsTab: "Contracts",
    basicInfo: "Basic Information",
    updateDetails: "Update your personal details",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phoneNumber: "Phone Number",
    saving: "Saving...",
    saveChanges: "Save Changes",
    expired: "Expired",
    expiringSoon: "Expiring Soon",
    active: "Active",
    download: "Download",
    renew: "Renew",
    startDate: "Start Date:",
    endDate: "End Date:",
    submitted: "Submitted:",
    language: "Language:",
    noContracts: "No contracts found",
    renewContract: "Renew Contract",
    signToRenew: "Sign to renew your contract for another year",
    currentPeriod: "Current period:",
    newPeriod: "New period:",
    renewing: "Renewing...",
    confirmRenewal: "Confirm Renewal",
    apartment: "Apartment:",
    profileUpdated: "Profile updated successfully!",
    failedUpdateProfile: "Failed to update profile",
    provideSignature: "Please provide your signature",
    contractRenewed: "Contract renewed successfully!",
    failedRenewContract: "Failed to renew contract",
    contractDownloaded: "Contract downloaded successfully!",
    failedDownloadContract: "Failed to download contract",
  },
  el: {
    backToDashboard: "Επιστροφή στον Πίνακα",
    profileSettings: "Ρυθμίσεις Προφίλ",
    profileTab: "Προφίλ",
    contractsTab: "Συμβόλαια",
    basicInfo: "Βασικές Πληροφορίες",
    updateDetails: "Ενημερώστε τα προσωπικά σας στοιχεία",
    firstName: "Όνομα",
    lastName: "Επώνυμο",
    email: "Email",
    phoneNumber: "Τηλέφωνο",
    saving: "Αποθήκευση...",
    saveChanges: "Αποθήκευση Αλλαγών",
    expired: "Έληξε",
    expiringSoon: "Λήγει Σύντομα",
    active: "Ενεργό",
    download: "Λήψη",
    renew: "Ανανέωση",
    startDate: "Ημερομηνία Έναρξης:",
    endDate: "Ημερομηνία Λήξης:",
    submitted: "Υποβλήθηκε:",
    language: "Γλώσσα:",
    noContracts: "Δεν βρέθηκαν συμβόλαια",
    renewContract: "Ανανέωση Συμβολαίου",
    signToRenew: "Υπογράψτε για να ανανεώσετε το συμβόλαιό σας για ένα ακόμη έτος",
    currentPeriod: "Τρέχουσα περίοδος:",
    newPeriod: "Νέα περίοδος:",
    renewing: "Ανανέωση...",
    confirmRenewal: "Επιβεβαίωση Ανανέωσης",
    apartment: "Διαμέρισμα:",
    profileUpdated: "Το προφίλ ενημερώθηκε με επιτυχία!",
    failedUpdateProfile: "Αποτυχία ενημέρωσης προφίλ",
    provideSignature: "Παρακαλώ δώστε την υπογραφή σας",
    contractRenewed: "Το συμβόλαιο ανανεώθηκε με επιτυχία!",
    failedRenewContract: "Αποτυχία ανανέωσης συμβολαίου",
    contractDownloaded: "Το συμβόλαιο λήφθηκε με επιτυχία!",
    failedDownloadContract: "Αποτυχία λήψης συμβολαίου",
  },
  he: {
    backToDashboard: "חזרה ללוח הבקרה",
    profileSettings: "הגדרות פרופיל",
    profileTab: "פרופיל",
    contractsTab: "חוזים",
    basicInfo: "מידע בסיסי",
    updateDetails: "עדכן את הפרטים האישיים שלך",
    firstName: "שם פרטי",
    lastName: "שם משפחה",
    email: "אימייל",
    phoneNumber: "מספר טלפון",
    saving: "שומר...",
    saveChanges: "שמור שינויים",
    expired: "פג תוקף",
    expiringSoon: "פג בקרוב",
    active: "פעיל",
    download: "הורדה",
    renew: "חידוש",
    startDate: "תאריך התחלה:",
    endDate: "תאריך סיום:",
    submitted: "הוגש:",
    language: "שפה:",
    noContracts: "לא נמצאו חוזים",
    renewContract: "חידוש חוזה",
    signToRenew: "חתום כדי לחדש את החוזה שלך לשנה נוספת",
    currentPeriod: "תקופה נוכחית:",
    newPeriod: "תקופה חדשה:",
    renewing: "מחדש...",
    confirmRenewal: "אישור חידוש",
    apartment: "דירה:",
    profileUpdated: "!הפרופיל עודכן בהצלחה",
    failedUpdateProfile: "עדכון הפרופיל נכשל",
    provideSignature: "אנא ספק את החתימה שלך",
    contractRenewed: "!החוזה חודש בהצלחה",
    failedRenewContract: "חידוש החוזה נכשל",
    contractDownloaded: "!החוזה הורד בהצלחה",
    failedDownloadContract: "הורדת החוזה נכשלה",
  }
};

const ClientProfile = ({ user, onBack, getToken, language = "en" }) => {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: user?.email || "",
    phone: ""
  });
  const [contracts, setContracts] = useState([]);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [renewingContract, setRenewingContract] = useState(null);
  const [signatureType, setSignatureType] = useState(null);
  const [signatureData, setSignatureData] = useState(null);

  const t = (key) => translations[language]?.[key] || translations.en[key];

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/contracts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/client/profile?` +
        `first_name=${encodeURIComponent(profileData.first_name)}&` +
        `last_name=${encodeURIComponent(profileData.last_name)}&` +
        `email=${encodeURIComponent(profileData.email)}&` +
        `phone=${encodeURIComponent(profileData.phone)}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        toast.success(t('profileUpdated'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('failedUpdateProfile'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('failedUpdateProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleRenewContract = async () => {
    if (!signatureType || !signatureData) {
      toast.error(t('provideSignature'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/contract-renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          original_contract_id: renewingContract.id,
          signature_type: signatureType,
          signature_data: signatureData
        })
      });

      if (response.ok) {
        toast.success(t('contractRenewed'));
        setShowRenewDialog(false);
        setRenewingContract(null);
        setSignatureType(null);
        setSignatureData(null);
        fetchContracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('failedRenewContract'));
      }
    } catch (error) {
      console.error('Error renewing contract:', error);
      toast.error(t('failedRenewContract'));
    } finally {
      setLoading(false);
    }
  };

  const getContractStatus = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const daysUntilExpiry = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { label: t('expired'), color: 'bg-red-100 text-red-800', canRenew: false };
    } else if (daysUntilExpiry <= 30) {
      return { label: t('expiringSoon'), color: 'bg-orange-100 text-orange-800', canRenew: true };
    } else {
      return { label: t('active'), color: 'bg-green-100 text-green-800', canRenew: false };
    }
  };

  const downloadContract = async (contract) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/client/contracts/${contract.id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${contract.template_name || 'Contract'}_${contract.id.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(t('contractDownloaded'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('failedDownloadContract'));
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error(t('failedDownloadContract'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir={language === 'he' ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" onClick={onBack} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <h1 className="text-2xl font-bold">{t('profileSettings')}</h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              {t('profileTab')}
            </TabsTrigger>
            <TabsTrigger value="contracts" data-testid="contracts-tab">
              <FileText className="w-4 h-4 mr-2" />
              {t('contractsTab')}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('basicInfo')}</CardTitle>
                <CardDescription>{t('updateDetails')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">{t('firstName')}</Label>
                      <Input
                        id="first_name"
                        data-testid="first-name-input"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">{t('lastName')}</Label>
                      <Input
                        id="last_name"
                        data-testid="last-name-input"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('phoneNumber')}</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+30 123 456 7890"
                    />
                  </div>
                  <Button type="submit" data-testid="save-profile-btn" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                    {loading ? t('saving') : t('saveChanges')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts" className="mt-6">
            <div className="space-y-4">
              {contracts.map((contract) => {
                const status = getContractStatus(contract.end_date);
                return (
                  <Card key={contract.id} data-testid={`contract-card-${contract.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="flex items-center gap-2 flex-wrap">
                            {contract.template_name || "Contract"}
                            <span className={`px-2 py-1 text-xs rounded ${status.color}`}>
                              {status.label}
                            </span>
                          </CardTitle>
                          <CardDescription>
                            {contract.apartment_name && `${t('apartment')} ${contract.apartment_name}`}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`download-contract-${contract.id}`}
                            onClick={() => downloadContract(contract)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t('download')}
                          </Button>
                          {status.canRenew && contract.status === 'active' && (
                            <Button
                              size="sm"
                              data-testid={`renew-contract-${contract.id}`}
                              className="bg-orange-600 hover:bg-orange-700"
                              onClick={() => {
                                setRenewingContract(contract);
                                setShowRenewDialog(true);
                              }}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              {t('renew')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">{t('startDate')}</span>
                          <p className="font-medium">{new Date(contract.start_date).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('endDate')}</span>
                          <p className="font-medium">{new Date(contract.end_date).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('submitted')}</span>
                          <p className="font-medium">{new Date(contract.submitted_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('language')}</span>
                          <p className="font-medium uppercase">{contract.language}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {contracts.length === 0 && (
                <Card className="bg-gray-50" data-testid="no-contracts-card">
                  <CardContent className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">{t('noContracts')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Renew Contract Dialog */}
        <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('renewContract')}</DialogTitle>
              <DialogDescription>
                {t('signToRenew')}
              </DialogDescription>
            </DialogHeader>
            {renewingContract && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                  <p className="font-semibold">{renewingContract.template_name}</p>
                  <p className="text-sm text-gray-600">
                    {t('currentPeriod')} {new Date(renewingContract.start_date).toLocaleDateString()} - {new Date(renewingContract.end_date).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {t('newPeriod')} {new Date().toLocaleDateString()} - {new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString()}
                  </p>
                </div>

                <SignatureCapture
                  onSignatureChange={(type, data) => {
                    setSignatureType(type);
                    setSignatureData(data);
                  }}
                  language={language}
                />

                <Button
                  onClick={handleRenewContract}
                  disabled={loading || !signatureType || !signatureData}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-renewal-btn"
                >
                  {loading ? t('renewing') : t('confirmRenewal')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ClientProfile;
